<template>
    <section class="section">
      <div class="columns">
        <div class="column is-half container">
        <b-field
          type="is-blue"
          expanded
          grouped
        >
          <b-input
          expanded
          :disabled="shouldDisplayLoading"
          :loading="shouldDisplayLoading"
          v-model="searchText"
          icon-pack="fas"
          icon="search"
          :placeholder="$t('Search')"
          />
          <p class="control">
            <b-button
            :loading="shouldDisplayLoading"
            v-if="canCreateShops"
            tag="router-link"
            type="is-primary"
            icon-pack="fas"
            icon-right="plus-square"
            :to="{ path: `/shops/admin/create/`}"
            />
          </p>
        </b-field>
      </div>
       </div>
      <b-modal
        v-model="isVisibleRelatedMembers"
        :can-cancel="true"
        has-modal-card
        aria-modal
        width="auto"
      >
        <memberDetailsModal
          :membersList="membersAdministrationListDetails"
          :loading="shouldDisplayLoading"
          :titleString="titleString"
          @close="closeRelatedMembersModal"
        />
      </b-modal>
       <b-modal
        v-model="isVisibleRelatedGroups"
        :can-cancel="true"
        has-modal-card
        aria-modal
        width="auto"
      >
        <groupDetailsModal
        :groupsList="relatedGroupsList"
        :showRelatedItems="false"
        :titleString="titleString"
        @close="closeRelatedGroupsModal"
        />
      </b-modal>
     <div class="container">
    <shopAdministrationDetails
      :shopsList="filteredShopsList"
      :loading="shouldDisplayLoading"
      :showRelatedItems="true"
      @member-count-clicked="memberSelected"
      @notification-group-count-clicked="groupSelected"
    />
     </div>
  </section>
</template>
<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAppStore } from '@/stores/app'
import shopsService from '@/services/shopsService'
import membersService from '@/services/membersService'
import notificationService from '@/services/notificationService'
import shopAdministrationDetails from '@/components/shopAdministrationDetails.vue'
import memberDetailsModal from '@/components/modals/memberDetailsModal.vue'
import groupDetailsModal from '@/components/modals/groupDetailsModal.vue'

import type { Shop as StoreShop, Member as StoreMember, NotificationGroup as StoreNotificationGroup } from '@/stores/app'

// Service-expected types
interface ServiceShop {
  shopId: number
  shopName: string
  chainId: number
  chainName?: string
  shoppingCenterId: number
  shoppingCenterName: string
  shoppingCenterGroupName: string
  sections: any[]
  creationDate: Date | string
  deactivationDate: Date | string | null
  chainShopId: string | null
  shoppingCenterShopId: string
  groupsRelated?: { groupId: number }[]
  membersRelated?: { memberId: number }[]
  canEditShop: boolean
}

interface ServiceMember {
  memberId: number
  memberFirstName: string
  memberEmail: string
  memberPhoneNumber: number
  memberPositionName: string
  memberComment: string
  groupName: string
  groupId: number
  shopsRelated: number[]
  notificationStrategy: number
  notificationStrategyName: string
  canUpdateMember: boolean
  email?: string
  phoneNumber?: number
  allowPhone?: boolean
  allowSms?: boolean
  allowWhatsapp?: boolean
  allowEmail?: boolean
  positionName?: string
  comment?: string
}

interface ServiceNotificationGroup {
  groupId: number
  groupName: string
  groupDescription: string
  canUpdateNotificationGroup: boolean
  shopIds: number[]
  groupType?: string
  members?: any[]
}

// Types for prepared data from services
interface PreparedShop {
  shopId: number
  shopName: string
  chainId: number
  chainName?: string
  shoppingCenterId: number
  shoppingCenterGroupName: string
  shoppingCenterName: string
  sections: any[]
  creationDate: string
  deactivationDate: string
  chainShopId: string | null
  shoppingCenterShopId: string
  groupsRelated: { groupId: number }[]
  membersRelated: { memberId: number }[]
  canEditShop: boolean
}

interface PreparedMember {
  memberId: number
  memberFirstName: string
  memberEmail: string
  memberPhoneNumber: number
  memberPositionName: string
  memberComment: string
  groupName: string
  groupId: number
  shopsRelated: number[]
  notificationStrategy: number
  notificationStrategyName: string
  canUpdateMember: boolean
}

// Type conversion utilities
const convertStoreShopToService = (storeShop: StoreShop): ServiceShop => ({
  shopId: storeShop.shopId,
  shopName: storeShop.shopName,
  chainId: storeShop.chainId ?? 0,
  chainName: storeShop.chainName,
  shoppingCenterId: storeShop.shoppingCenterId ?? 0,
  shoppingCenterName: storeShop.shoppingCenterName ?? '',
  shoppingCenterGroupName: storeShop.shoppingCenterGroupName ?? '',
  sections: storeShop.sections ?? [],
  creationDate: storeShop.creationDate,
  deactivationDate: storeShop.deactivationDate,
  chainShopId: storeShop.chainShopId ?? null,
  shoppingCenterShopId: storeShop.shoppingCenterShopId ?? '',
  groupsRelated: storeShop.groupsRelated ?? [],
  membersRelated: storeShop.membersRelated ?? [],
  canEditShop: storeShop.canEditShop ?? false
})

const convertStoreMemberToService = (storeMember: StoreMember): ServiceMember => ({
  memberId: storeMember.memberId ?? 0,
  memberFirstName: storeMember.memberFirstName ?? '',
  memberEmail: storeMember.memberEmail ?? storeMember.email ?? '',
  memberPhoneNumber: storeMember.memberPhoneNumber ?? storeMember.phoneNumber ?? 0,
  memberPositionName: storeMember.memberPositionName ?? storeMember.positionName ?? '',
  memberComment: storeMember.memberComment ?? storeMember.comment ?? '',
  groupName: storeMember.groupName ?? '',
  groupId: storeMember.groupId ?? 0,
  shopsRelated: storeMember.shopsRelated ?? [],
  notificationStrategy: storeMember.notificationStrategy ?? 0,
  notificationStrategyName: storeMember.notificationStrategyName ?? '',
  canUpdateMember: storeMember.canUpdateMember ?? false,
  email: storeMember.email,
  phoneNumber: storeMember.phoneNumber,
  allowPhone: storeMember.allowPhone,
  allowSms: storeMember.allowSms,
  allowWhatsapp: storeMember.allowWhatsapp,
  allowEmail: storeMember.allowEmail,
  positionName: storeMember.positionName,
  comment: storeMember.comment
})

const convertStoreGroupToService = (storeGroup: StoreNotificationGroup): ServiceNotificationGroup => ({
  groupId: storeGroup.groupId,
  groupName: storeGroup.groupName,
  groupDescription: storeGroup.groupDescription,
  canUpdateNotificationGroup: storeGroup.canUpdateNotificationGroup ?? false,
  shopIds: storeGroup.shopIds,
  groupType: storeGroup.groupType,
  members: storeGroup.members
})

// Composables
const { t } = useI18n()
const store = useAppStore()

// Reactive data
const searchText = ref<string>('')
const isVisibleRelatedMembers = ref<boolean>(false)
const isVisibleRelatedGroups = ref<boolean>(false)
const relatedMemberList = ref<StoreMember[]>([])
const relatedGroupsList = ref<StoreNotificationGroup[]>([])
const selectedShop = ref<StoreShop | null>(null)

const closeRelatedMembersModal = (): void => {
  isVisibleRelatedMembers.value = false
}

const closeRelatedGroupsModal = (): void => {
  isVisibleRelatedGroups.value = false
}

// Computed properties
const titleString = computed<string>(() => {
  return selectedShop.value
    ? `${t('Shop')} ${selectedShop.value.shopId} ${selectedShop.value.shopName}`
    : ''
})

const shouldDisplayLoading = computed<boolean>(() => {
  const shopList = store.getAllowedShopsList
  return !Array.isArray(shopList) || !shopList.length
})

const canCreateShops = computed<boolean>(() => {
  const shopList = store.getAllowedShopsList
  if (!shopList) return false
  
  const serviceShops: ServiceShop[] = shopList.map(convertStoreShopToService)
  return shopsService.canCreateShops(serviceShops)
})

const relatedMembersModalReady = computed<boolean>(() => {
  return (
    Array.isArray(store.getMemberList) &&
    Array.isArray(store.getNotificationGroups) &&
    Array.isArray(store.getStrategies)
  )
})

const shopsAdministrationListDetails = computed<PreparedShop[]>(() => {
  const shopList = store.getAllowedShopsList
  const notificationGroupList = store.getNotificationGroups
  const membersList = store.getMemberList
  const chainsList = store.getChains
  
  if (!shopList) return []
  
  const serviceShops: ServiceShop[] = shopList.map(convertStoreShopToService)
  const serviceGroups: ServiceNotificationGroup[] = (notificationGroupList || []).map(convertStoreGroupToService)
  
  // Convert members to the simple format expected by shopsService
  const simpleMemberList = (membersList || [])
    .filter((member): member is StoreMember & { memberId: number; groupId: number } => 
      member.memberId !== undefined && member.groupId !== undefined
    )
    .map((member): { memberId: number; groupId: number } => ({
      memberId: member.memberId,
      groupId: member.groupId
    }))
  
  const data = shopsService.prepareShopsData(
    serviceShops,
    serviceGroups,
    simpleMemberList,
    chainsList || []
  )
  return data as PreparedShop[]
})

const filteredShopsList = computed<PreparedShop[]>(() => {
  const filteredShops = shopsAdministrationListDetails.value.filter((shop: PreparedShop) =>
    searchText.value
      .toUpperCase()
      .split(/[ ]+/u)
      .filter((el: string) => el)
      .every((phrase: string) =>
        (shop.shoppingCenterGroupName || '')
          .concat(shop.shoppingCenterName || '')
          .concat(shop.shopName || '')
          .concat(shop.shoppingCenterShopId || '')
          .concat(shop.shopId.toString())
          .toUpperCase()
          .includes(phrase)
      )
  )
  return filteredShops
})

const membersAdministrationListDetails = computed<PreparedMember[]>(() => {
  if (!relatedMembersModalReady.value) {
    return []
  }

  // Filter out members without required memberId and convert to service format
  const validMembers = relatedMemberList.value.filter((member: StoreMember): member is StoreMember & { memberId: number } => 
    member.memberId !== undefined
  )
  
  const serviceMembers = validMembers.map((member): ServiceMember => {
    const converted = convertStoreMemberToService(member)
    return {
      ...converted,
      memberId: member.memberId // Ensure memberId is present
    }
  })
  
  const serviceGroups: ServiceNotificationGroup[] = (store.getNotificationGroups || []).map(convertStoreGroupToService)
  
  const data = membersService.prepareMembersDetails(
    serviceMembers,
    serviceGroups,
    store.getStrategies || []
  )
  return data as PreparedMember[]
})

// Methods
const groupSelected = (shopId: number): void => {
  const shopList = store.getAllowedShopsList
  const notificationGroupList = store.getNotificationGroups
  
  selectedShop.value = shopList?.find((shop: StoreShop) => shop.shopId === shopId) || null
  relatedGroupsList.value = notificationGroupList?.filter((group: StoreNotificationGroup) => 
    group.shopIds.includes(shopId)
  ) || []
  isVisibleRelatedGroups.value = true
}

const memberSelected = (shopId: number): void => {
  if (!relatedMembersModalReady.value) {
    return
  }

  const shopList = store.getAllowedShopsList
  const notificationGroupList = store.getNotificationGroups
  const membersList = store.getMemberList
  
  selectedShop.value = shopList?.find((shop: StoreShop) => shop.shopId === shopId) || null
  
  const relatedGroups = notificationGroupList
    ? notificationGroupList
        .filter((group: StoreNotificationGroup) => group.shopIds.includes(shopId))
        .map((item: StoreNotificationGroup) => item.groupId)
    : []
  
  const relatedMembers = membersList
    ? membersList.filter((member: StoreMember) =>
        relatedGroups.includes(member.groupId || 0)
      )
    : []
  
  relatedMemberList.value = relatedMembers
  
  if (relatedMembers.length > 0) {
    isVisibleRelatedMembers.value = true
  }
}

// Lifecycle
onMounted(async (): Promise<void> => {
  try {
    if (!store.getNotificationGroups) {
      await notificationService.getNotificationGroups()
    }
    if (!store.getMemberList) {
      await membersService.getMembersList()
    }
    if (!store.getAllowedShopsList) {
      await shopsService.getAllowedShopsList()
    }
    if (!store.getStrategies) {
      await notificationService.getStrategies()
    }
    if (!store.getChains) {
      await shopsService.getChains()
    }
  } catch (error) {
    console.error('Failed to load initial data:', error)
  }
})
</script>
